import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { ChartData, ChartOptions, ChartType } from 'chart.js';

@Component({
  standalone: true,
  selector: 'app-stat-chart',
  templateUrl: './stat-chart.component.html',
  styleUrls: ['./stat-chart.component.scss'],
  imports: [CommonModule, ChartModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatChartComponent implements OnChanges {
  @Input() type:
    | 'line'
    | 'bar'
    | 'pie'
    | 'doughnut'
    | 'radar'
    | 'polarArea'
    | 'bubble'
    | 'scatter' = 'line';
  @Input() data: ChartData | null = null;
  @Input() options: ChartOptions | null = null;
  @Input() height: string = '300px';
  @Input() loading: boolean = false;
  @Input() empty: boolean = false;
  @Input() emptyMessage: string = 'STATISTICS.GENERAL.NO_DATA_AVAILABLE';
  @Input() emptyIcon: string = 'pi-chart-bar';

  chartType: ChartType = 'line';
  chartOptions: ChartOptions = {};

  // Default chart options with consistent styling
  private defaultOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#334155',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          weight: 'bold',
          size: 13,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 11,
          },
        },
      },
      y: {
        grid: {
          color: '#f1f5f9',
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 11,
          },
        },
        beginAtZero: true,
      },
    },
  };

  // Default options for pie/doughnut charts
  private pieOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          color: '#334155',
          font: {
            size: 12,
          },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#334155',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      },
    },
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type'] || changes['options']) {
      this.updateChartOptions();
    }
  }

  private updateChartOptions(): void {
    this.chartType = this.type as ChartType;

    // Merge default options with custom options
    if (
      this.type === 'pie' ||
      this.type === 'doughnut' ||
      this.type === 'polarArea'
    ) {
      this.chartOptions = {
        ...this.pieOptions,
        ...(this.options || {}),
      };
    } else {
      this.chartOptions = {
        ...this.defaultOptions,
        ...(this.options || {}),
      };
    }
  }
}
